import { GraphQlRequestService } from '../../../api/graphql-request-service';
import {
  FrontendStartupForSimulationQuery,
  FrontendStartupQuery,
} from '../../../graphql/generated/graphql-sdk';
import { AssignedPermissions } from '../../../permissions/permissions';
import { DashboardId } from '../../common/components/dashboards/types';
import { GraphQLError } from '../../common/errors';
import { Domains } from '../../constants';
import {
  GraphQlRequestErrorCode,
  HasDependencies,
  hashString,
  Role,
} from '../types';
import { handleGQLErrors } from '../utils';
import {
  FrontendStartup,
  FrontendStartupSimulatedRole,
  InitialDependenciesGeneralError,
  InitialDependenciesIpForbiddenError,
  InitialDependenciesNetworkError,
  InitialDependenciesUserNotFoundError,
  InitialDependenciesWrongAuthMethodError,
} from './types';

export class FrontendStartupService implements HasDependencies {
  constructor(readonly graphQlRequestService: GraphQlRequestService) {}

  getDeps = (): (string | undefined)[] => [
    hashString([...this.graphQlRequestService.getDeps()]),
  ];

  get = async (): Promise<FrontendStartup | null> => {
    const result = await handleGQLErrors(
      this.graphQlRequestService.graphQlSdk.frontendStartup()
    )
      .then(this.mapResult)
      .catch(this.handleError);

    return result as FrontendStartup | null;
  };

  getForSimulation = async (
    simulatedRole: Role
  ): Promise<FrontendStartupSimulatedRole | null> => {
    const result = await handleGQLErrors(
      this.graphQlRequestService.graphQlSdk.frontendStartupForSimulation({
        roleId: simulatedRole.id,
        simulateRole: simulatedRole.id,
      })
    )
      .then(this.mapResultForSimulation)
      .catch(this.handleError);
    return result as FrontendStartupSimulatedRole | null;
  };

  private handleError = (error: GraphQLError) => {
    const errorMessage: string = error.message ?? '';
    const errorCode = error.errorCode;
    const networkRequestFailedRegex = /Network request failed/i;
    if (networkRequestFailedRegex.test(errorMessage)) {
      throw new InitialDependenciesNetworkError(error);
    } else if (errorCode === GraphQlRequestErrorCode.Err_UserNotFound) {
      throw new InitialDependenciesUserNotFoundError(error);
    } else if (
      errorCode ===
      GraphQlRequestErrorCode.Err_DomainAccessDeniedWrongAuthMethod
    ) {
      throw new InitialDependenciesWrongAuthMethodError(error);
    } else if (
      errorCode === GraphQlRequestErrorCode.Err_DomainAccessDeniedIpForbidden
    ) {
      throw new InitialDependenciesIpForbiddenError(error);
    } else {
      // Optimally we should check the errorCode of the backend to see for what reason it failed
      throw new InitialDependenciesGeneralError(error);
    }
  };

  private mapResult = (result: FrontendStartupQuery): FrontendStartup => {
    const currentUser = result.currentUser;
    const userRole = currentUser?.userRole;
    const allPermissions = userRole?.allPermissions;
    if (!currentUser || !userRole || !allPermissions) {
      throw new InitialDependenciesUserNotFoundError(
        new GraphQLError(
          new Error('User not found'),
          null,
          GraphQlRequestErrorCode.Err_UserNotFound
        )
      );
    }

    if (!userRole.allowedDomains) {
      throw new Error('Error getting allowedDomains for user role');
    }

    return {
      ...result,
      listAllDashboards:
        result.listAllDashboards?.map((d) => ({
          ...d,
          pages: d.pages.map((p) => ({
            ...p,
            dashboardId: d.id as DashboardId,
          })),
        })) ?? [],
      currentUser: {
        ...currentUser,
        userRole: {
          ...userRole,
          allowedDomains: userRole.allowedDomains.map((allowedDomain) => ({
            domain: allowedDomain.domain as Domains,
            latestPreferences: allowedDomain.latestPreferences!,
            latestSettings: allowedDomain.latestSettings!,
          })),
          // TODO: This is a hack to avoid the type instantiation being excessively deep and possibly infinite.
          // We should find a better way to handle this.
          // @ts-ignore
          allPermissions: new AssignedPermissions(
            allPermissions.map((p) => ({
              id: p.id,
              roleId: p.roleId,
              permission: p.permission,
            }))
          ),
          preferencesLatestVersion: userRole.preferencesLatestVersion,
        },
      },
    };
  };

  private mapResultForSimulation = (
    result: FrontendStartupForSimulationQuery
  ): FrontendStartupSimulatedRole => {
    const userRole = result.userRoleForRoleId;
    const allPermissions = userRole?.allPermissions;
    if (!userRole || !allPermissions) {
      throw new InitialDependenciesUserNotFoundError(
        new GraphQLError(
          new Error('User not found'),
          null,
          GraphQlRequestErrorCode.Err_UserNotFound
        )
      );
    }

    if (!userRole.allowedDomains) {
      throw new Error('Error getting allowedDomains for simulated role');
    }

    return {
      ...result,
      listAllDashboards:
        result.listAllDashboards?.map((d) => ({
          ...d,
          pages: d.pages.map((p) => ({
            ...p,
            dashboardId: d.id as DashboardId,
          })),
        })) ?? [],
      simulatedRole: {
        ...userRole,
        allowedDomains: userRole.allowedDomains.map((allowedDomain) => ({
          domain: allowedDomain.domain as Domains,
          latestPreferences: allowedDomain.latestPreferences!,
          latestSettings: allowedDomain.latestSettings!,
        })),
        // TODO: This is a hack to avoid the type instantiation being excessively deep and possibly infinite.
        // We should find a better way to handle this.
        // @ts-ignore
        allPermissions: new AssignedPermissions(
          allPermissions.map((p) => ({
            id: p.id,
            roleId: p.roleId,
            permission: p.permission,
          }))
        ),
        preferencesLatestVersion: userRole.preferencesLatestVersion,
      },
    };
  };
}
